/* ==========================================================================
   AJ FITS — Product page
   Gallery, variant picker, hover zoom, lightbox, tabs, quantity,
   sticky add-to-cart, share.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function $(s, r) { return (r || document).querySelector(s); }
  function $$(s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); }

  function toast(msg, opts) {
    if (window.AJ && window.AJ.toast) window.AJ.toast(msg, opts);
  }

  /*
    aj-stock.js owns every inventory ceiling. If it ever fails to load the
    stub below keeps the page working — uncapped, exactly as it behaved
    before, with Shopify's own server-side check as the backstop.
  */
  var stockStub = {
    // Real parse, so a stock ceiling read off the markup survives even
    // without the module — only the cart-aware part degrades.
    parse: function (value) {
      if (value === undefined || value === null || value === '') return Infinity;
      var n = parseInt(value, 10);
      return isNaN(n) || n < 0 ? Infinity : n;
    },
    inCart: function () { return 0; },
    remaining: function () { return Infinity; },
    allow: function () { return true; },
    warn: function () {},
    clearNote: function () {},
    friendlyError: function (err) { return (err && err.message) || 'Could not add to cart'; }
  };

  function stockApi() {
    return (window.AJ && window.AJ.stock) || stockStub;
  }

  function init(root) {
    $$('[data-aj-product]', root).forEach(function (pdp) {
      if (pdp.dataset.ajPdpReady) return;
      pdp.dataset.ajPdpReady = '1';

      var gallery = new Gallery(pdp);
      var quantity = new Quantity(pdp);
      new VariantPicker(pdp, gallery, quantity);
      new Tabs(pdp);
      new StickyAtc(pdp);
      new Lightbox(pdp, gallery);
      initShare(pdp);
      trackRecentlyViewed(pdp);
    });
  }

  /* =====================================================================
     Gallery
     ===================================================================== */
  function Gallery(pdp) {
    this.el = $('[data-aj-gallery]', pdp);
    if (!this.el) return;

    this.slides = $$('[data-aj-gallery-slide]', this.el);
    this.thumbs = $$('[data-aj-gallery-thumb]', this.el);
    this.counter = $('[data-aj-gallery-current]', this.el);
    this.index = 0;

    this.bind();
    this.initZoom();
    this.initScan();
  }

  Gallery.prototype.bind = function () {
    var self = this;

    this.thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { self.go(i); });
    });

    var prev = $('[data-aj-gallery-prev]', this.el);
    var next = $('[data-aj-gallery-next]', this.el);
    if (prev) prev.addEventListener('click', function () { self.go(self.index - 1); });
    if (next) next.addEventListener('click', function () { self.go(self.index + 1); });

    // Keyboard within the gallery
    this.el.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); self.go(self.index - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); self.go(self.index + 1); }
    });

    // Touch swipe
    var startX = 0, startY = 0;
    var viewport = $('[data-aj-gallery-viewport]', this.el);
    if (viewport) {
      viewport.addEventListener('touchstart', function (e) {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });

      viewport.addEventListener('touchend', function (e) {
        var dx = e.changedTouches[0].clientX - startX;
        var dy = e.changedTouches[0].clientY - startY;
        // Ignore mostly-vertical gestures so page scroll still works
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          self.go(dx < 0 ? self.index + 1 : self.index - 1);
        }
      }, { passive: true });
    }
  };

  Gallery.prototype.go = function (n) {
    if (!this.slides.length) return;
    this.index = (n + this.slides.length) % this.slides.length;

    this.slides.forEach(function (s, i) {
      var on = i === this.index;
      s.classList.toggle('is-active', on);
      // Stop any video that scrolls out of the active slot
      if (!on) {
        var v = s.querySelector('video');
        if (v && !v.paused) v.pause();
      }
    }, this);

    this.thumbs.forEach(function (t, i) {
      var on = i === this.index;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      if (on && t.scrollIntoView) t.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, this);

    if (this.counter) this.counter.textContent = this.index + 1;
    this.el.dispatchEvent(new CustomEvent('aj:gallery:change', { detail: { index: this.index } }));
  };

  /* Hover zoom — swaps in a high-res background and tracks the pointer */
  Gallery.prototype.initZoom = function () {
    if (this.el.dataset.zoom !== 'true') return;
    if (window.matchMedia('(hover: none)').matches) return;

    $$('[data-aj-zoom]', this.el).forEach(function (zone) {
      var src = zone.dataset.zoomSrc;
      if (!src) return;
      var loaded = false;

      zone.addEventListener('mouseenter', function () {
        if (!loaded) {
          // Only fetch the 2000px asset when the shopper actually hovers
          var pre = new Image();
          pre.src = src;
          pre.onload = function () {
            zone.style.backgroundImage = 'url(' + src + ')';
            loaded = true;
          };
        }
        zone.classList.add('is-zoomed');
      });

      zone.addEventListener('mousemove', function (e) {
        var rect = zone.getBoundingClientRect();
        var x = ((e.clientX - rect.left) / rect.width) * 100;
        var y = ((e.clientY - rect.top) / rect.height) * 100;
        zone.style.backgroundPosition = x + '% ' + y + '%';
      });

      zone.addEventListener('mouseleave', function () {
        zone.classList.remove('is-zoomed');
      });
    });
  };

  /* Scan effect plays once when the gallery first enters view */
  Gallery.prototype.initScan = function () {
    if (this.el.dataset.scan !== 'true' || reduceMotion) return;
    if (!('IntersectionObserver' in window)) return;

    var el = this.el;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          el.classList.add('is-scanning');
          io.unobserve(el);
          // Leave the class on so the chips stay visible after the sweep
        });
      },
      { threshold: 0.35 }
    );
    io.observe(el);
  };

  /* =====================================================================
     Variant picker
     ===================================================================== */
  function VariantPicker(pdp, gallery, quantity) {
    this.pdp = pdp;
    this.gallery = gallery;
    this.quantity = quantity;

    var data = $('[data-aj-variants]', pdp);
    if (!data) return;

    try {
      this.variants = JSON.parse(data.textContent);
    } catch (e) {
      return;
    }

    this.inputs = $$('[data-aj-variant-option]', pdp);
    this.idField = $('[data-aj-variant-id]', pdp);
    this.priceNow = $('[data-aj-price-now]', pdp);
    this.priceWas = $('[data-aj-price-was]', pdp);
    this.priceSave = $('[data-aj-price-save]', pdp);
    this.atc = $('[data-aj-atc]', pdp);
    this.atcText = $('[data-aj-atc-text]', pdp);
    this.stickyPrice = $('[data-aj-sticky-price]', pdp);
    this.stickyBtn = $('[data-aj-sticky-btn]', pdp);
    this.inventory = $('[data-aj-inventory]', pdp);
    this.buyWrap = $('[data-aj-buy]', pdp);
    this.notify = $('[data-aj-notify]', pdp);
    this.notifyBody = $('[data-aj-notify-body]', pdp);
    this.productTitle = (function () {
      var h = $('.aj-pdp__title', pdp);
      return h ? h.textContent.trim() : '';
    })();

    this.bind();
    this.update();
  }

  VariantPicker.prototype.bind = function () {
    var self = this;
    this.inputs.forEach(function (input) {
      input.addEventListener('change', function () { self.update(); });
    });

    var form = $('.aj-pdp__form', this.pdp);
    if (form) {
      form.addEventListener('submit', function (e) { self.submit(e, form); });
    }
  };

  VariantPicker.prototype.selected = function () {
    var chosen = [];
    this.inputs.forEach(function (input) {
      if (input.checked) chosen.push(input.value);
    });
    return chosen;
  };

  VariantPicker.prototype.update = function () {
    var chosen = this.selected();

    // Reflect the picked value in each option legend
    var groups = $$('[data-aj-option-value]', this.pdp);
    groups.forEach(function (el, i) {
      if (chosen[i]) el.textContent = chosen[i];
    });

    var match = null;
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      var ok = true;
      for (var j = 0; j < chosen.length; j++) {
        if (v.options[j] !== chosen[j]) { ok = false; break; }
      }
      if (ok) { match = v; break; }
    }

    this.current = match;

    if (!match) {
      if (this.atc) this.atc.disabled = true;
      if (this.atcText) this.atcText.textContent = 'Unavailable';
      if (this.idField) this.idField.disabled = true;
      return;
    }

    if (this.idField) {
      this.idField.value = match.id;
      this.idField.disabled = !match.available;
    }

    // Re-cap the stepper for the variant now selected.
    if (this.quantity && this.quantity.setVariant) {
      this.quantity.setVariant(match.id, match.stock_max);
    }

    if (this.priceNow) this.priceNow.textContent = match.price;
    if (this.priceWas) {
      if (match.compare_at_price) {
        this.priceWas.textContent = match.compare_at_price;
        this.priceWas.hidden = false;
      } else {
        this.priceWas.hidden = true;
      }
    }
    if (this.priceSave) this.priceSave.hidden = !match.compare_at_price;
    if (this.stickyPrice) this.stickyPrice.textContent = match.price;

    var label = match.available
      ? (window.ajStrings && window.ajStrings.addToCart) || 'Add to cart'
      : (window.ajStrings && window.ajStrings.soldOut) || 'Sold out';

    if (this.atc) this.atc.disabled = !match.available;
    if (this.atcText) this.atcText.textContent = label;
    if (this.stickyBtn) {
      this.stickyBtn.disabled = !match.available;
      this.stickyBtn.textContent = label;
    }

    // Swap the buy form for the back-in-stock form on sold-out variants
    if (this.notify && this.buyWrap) {
      this.notify.hidden = match.available;
      this.buyWrap.hidden = !match.available;

      if (!match.available && this.notifyBody) {
        this.notifyBody.value =
          'Back-in-stock request for ' + this.productTitle +
          ' (' + chosen.join(' / ') + ') — ' + window.location.href;
      }
    }

    // Jump the gallery to the image tied to this variant
    if (this.gallery && match.featured_media_position !== null && this.gallery.go) {
      this.gallery.go(match.featured_media_position);
    }

    this.updateUrl(match.id);
  };

  VariantPicker.prototype.updateUrl = function (id) {
    if (!window.history.replaceState) return;
    var url = new URL(window.location.href);
    url.searchParams.set('variant', id);
    window.history.replaceState({}, '', url.toString());
  };

  VariantPicker.prototype.submit = function (e, form) {
    e.preventDefault();
    var btn = this.atc;
    if (!btn || btn.disabled) return;

    var stock = stockApi();
    var qtyInput = this.quantity && this.quantity.input;
    var wanted = qtyInput ? Number(qtyInput.value) || 1 : 1;
    var cap = this.current ? this.current.stock_max : null;

    // Refuse before the request when the cart already holds the shelf.
    var note = this.quantity ? this.quantity.note : null;
    if (this.current && !stock.allow(this.current.id, cap, wanted, note)) return;

    btn.classList.add('is-loading');
    btn.disabled = true;
    var spinner = $('.aj-pdp__atc-spinner', btn);
    if (spinner) spinner.hidden = false;

    var formData = new FormData(form);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw new Error(data.description || data.message || 'Could not add to cart');
          return data;
        });
      })
      .then(function () {
        // refreshBadge re-syncs AJ.stock, which re-caps the stepper.
        if (window.AJ && window.AJ.cart) window.AJ.cart.refreshBadge();
        stock.clearNote(note);
        toast('Added to cart', { action: { href: '/cart', label: 'View cart' } });

        var drawer = document.querySelector('cart-drawer');
        if (drawer) {
          fetch('/?sections=cart-drawer,cart-icon-bubble', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (sections) {
              if (typeof drawer.renderContents === 'function') {
                drawer.renderContents({ sections: sections, sections_html: sections });
              }
            })
            .catch(function () {});
        }
      })
      .catch(function (err) {
        // Shopify's own oversell rejection, re-said in the theme's wording.
        var message = stock.friendlyError(err);
        if (note) {
          note.textContent = message;
          note.hidden = false;
        }
        toast(message, { type: 'error' });
        if (window.AJ && window.AJ.stock) window.AJ.stock.refresh();
      })
      .finally(function () {
        btn.classList.remove('is-loading');
        btn.disabled = false;
        if (spinner) spinner.hidden = true;
      });
  };

  /* =====================================================================
     Tabs
     ===================================================================== */
  function Tabs(pdp) {
    var wrap = $('[data-aj-tabs]', pdp);
    if (!wrap) return;

    var tabs = $$('[data-aj-tab]', wrap);
    var panels = $$('[data-aj-tab-panel]', wrap);

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var idx = tab.dataset.ajTab;
        tabs.forEach(function (t) {
          var on = t === tab;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        panels.forEach(function (p) {
          p.classList.toggle('is-active', p.dataset.ajTabPanel === idx);
        });
      });

      tab.addEventListener('keydown', function (e) {
        var i = tabs.indexOf(tab);
        var next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = tabs[(i + 1) % tabs.length];
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = tabs[(i - 1 + tabs.length) % tabs.length];
        if (next) { e.preventDefault(); next.focus(); next.click(); }
      });
    });
  }

  /* =====================================================================
     Quantity

     The ceiling is the selected variant's stock minus whatever is already in
     the cart, so a shopper holding 2 of a 3-unit variant can only add 1 more.
     Anything that would go over is clamped and explained rather than silently
     ignored.
     ===================================================================== */
  function Quantity(pdp) {
    this.input = $('[data-aj-qty-input]', pdp);
    if (!this.input) return;

    this.down = $('[data-aj-qty-down]', pdp);
    this.up = $('[data-aj-qty-up]', pdp);
    this.note = $('[data-aj-qty-note]', pdp);

    // Stock on the shelf for the current variant; Infinity when uncapped.
    this.stockMax = stockApi().parse(this.input.getAttribute('max'));
    this.variantId = null;

    var self = this;

    if (this.down) {
      this.down.addEventListener('click', function () { self.set(Number(self.input.value) - 1); });
    }
    if (this.up) {
      this.up.addEventListener('click', function () { self.set(Number(self.input.value) + 1, true); });
    }
    this.input.addEventListener('change', function () { self.set(Number(self.input.value) || 1, true); });

    // A cart change elsewhere on the page moves the ceiling.
    document.addEventListener('aj:stock:change', function () { self.applyMax(); });

    this.applyMax();
  }

  /* The most that may be added right now, given what the cart already holds. */
  Quantity.prototype.ceiling = function () {
    if (this.stockMax === Infinity) return Infinity;
    return Math.max(1, this.stockMax - stockApi().inCart(this.variantId));
  };

  Quantity.prototype.setVariant = function (variantId, stockMax) {
    if (!this.input) return;
    this.variantId = variantId;
    this.stockMax = stockApi().parse(stockMax === null || stockMax === undefined ? '' : stockMax);
    this.applyMax();
  };

  /* Push the current ceiling onto the input and clamp the value into range. */
  Quantity.prototype.applyMax = function (announce) {
    if (!this.input) return;

    var max = this.ceiling();
    if (max === Infinity) {
      this.input.removeAttribute('max');
    } else {
      this.input.max = max;
    }

    this.set(Number(this.input.value) || 1, announce);
  };

  Quantity.prototype.set = function (value, announce) {
    var stock = stockApi();
    var min = parseInt(this.input.min, 10) || 1;
    var max = this.ceiling();
    var clamped = Math.min(Math.max(value, min), max);

    this.input.value = clamped;

    if (this.down) this.down.disabled = clamped <= min;
    if (this.up) this.up.disabled = max !== Infinity && clamped >= max;

    if (announce && max !== Infinity && value > max) {
      stock.warn(this.stockMax, this.note, stock.inCart(this.variantId));
    } else if (value <= max) {
      stock.clearNote(this.note);
    }

    return clamped;
  };

  /* =====================================================================
     Sticky add to cart
     ===================================================================== */
  function StickyAtc(pdp) {
    var bar = $('[data-aj-sticky-atc]', pdp);
    var atc = $('[data-aj-atc]', pdp);
    if (!bar || !atc || !('IntersectionObserver' in window)) return;

    bar.hidden = false;
    document.body.classList.add('aj-has-sticky-atc');

    // Show the bar only once the real button has scrolled out of view
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      },
      { threshold: 0 }
    );
    io.observe(atc);
  }

  /* =====================================================================
     Lightbox
     ===================================================================== */
  function Lightbox(pdp, gallery) {
    var box = $('[data-aj-lightbox]', pdp);
    if (!box) return;

    var slides = $$('[data-aj-lightbox-slide]', box);
    var index = 0;

    function go(n) {
      if (!slides.length) return;
      index = (n + slides.length) % slides.length;
      slides.forEach(function (s, i) {
        s.classList.toggle('is-active', i === index);
        s.classList.remove('is-zoomed');
      });
    }

    $$('[data-aj-lightbox-open]', pdp).forEach(function (btn) {
      btn.addEventListener('click', function () {
        go(gallery && typeof gallery.index === 'number' ? gallery.index : 0);
        if (window.AJ && window.AJ.openModal) window.AJ.openModal('AjLightbox');
      });
    });

    var prev = $('[data-aj-lightbox-prev]', box);
    var next = $('[data-aj-lightbox-next]', box);
    if (prev) prev.addEventListener('click', function () { go(index - 1); });
    if (next) next.addEventListener('click', function () { go(index + 1); });

    slides.forEach(function (img) {
      img.addEventListener('click', function () { img.classList.toggle('is-zoomed'); });
    });

    document.addEventListener('keydown', function (e) {
      if (box.hidden) return;
      if (e.key === 'ArrowLeft') go(index - 1);
      if (e.key === 'ArrowRight') go(index + 1);
    });

    // Swipe between images on touch
    var startX = 0;
    box.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
    box.addEventListener('touchend', function (e) {
      var dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 50) go(dx < 0 ? index + 1 : index - 1);
    }, { passive: true });
  }

  /* =====================================================================
     Share
     ===================================================================== */
  function initShare(pdp) {
    $$('[data-aj-share]', pdp).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var url = btn.dataset.url || window.location.href;
        var title = btn.dataset.title || document.title;

        if (navigator.share) {
          navigator.share({ title: title, url: url }).catch(function () {});
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(function () {
            toast('Link copied');
          });
        }
      });
    });
  }

  /* =====================================================================
     Recently viewed
     ===================================================================== */
  function trackRecentlyViewed(pdp) {
    if (!window.AJ || !window.AJ.recentlyViewed) return;
    var wish = $('[data-aj-wishlist-toggle]', pdp);
    if (!wish) return;

    window.AJ.recentlyViewed.push({
      handle: wish.dataset.handle,
      title: wish.dataset.title,
      url: wish.dataset.url,
      image: wish.dataset.image,
      price: wish.dataset.price
    });
  }

  /* =====================================================================
     Boot
     ===================================================================== */
  function boot() { init(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  // Expose for the quick-view modal, which injects product markup later
  window.AJProduct = { init: init };
})();
